import React, { useState } from 'react';
import TopBar from '../../Components/TopBar';
import Page from '../../Components/Containers/Page';
import Search, { SearchBar, useSearch } from '../../Components/Search';
import { useBusinesses } from '../../Data/Admin';
import Dropdown from '../../Components/Dropdown';
import Grid from '../../Components/Formatting/Grid';
import NoValue from '../../Components/EmptyStates/NoValue';
import Money from '../../Components/Formatting/Money';
import TimeAgo from '../../Components/Formatting/TimeAgo';
import API from '../../Services/API';
import { useToasts } from '../../Components/Toasts';
import SendNotification from './Actions/SendNotification';
import { useDialogues } from '../../Components/Dialogues';
import BusinessInfo from './Actions/BusinessInfo';

const Businesses = () => {
    return <>
        <TopBar left="/admin" title="Businesses" />
        <Page>
            <Search persist="business:search">
                <Inner />
            </Search>
        </Page>
    </>
}

const Inner = () => {
    const {searchDebounced, liveSearch} = useSearch();
    const {businesses, isLoading} = useBusinesses(searchDebounced.search ?? '');
    return <>
        <SearchBar isLoading={isLoading} />

        <ul className="divide-y divide-gray-200">
            {businesses && businesses.filter(liveSearch).map(business => <Business key={business.id} {...{business}} />)}
        </ul>
    </>
}

const Business = ({business}) => {
    const [switching, setSwitching] = useState(false);
    const {showSuccess, showError} = useToasts();
    const [action, setAction] = useState(null);
    const {confirm} = useDialogues();

    const switchInto = async () => {
        setSwitching(true);
        API.switchIntoBusiness(business.id).then(() => {
            showSuccess('Switching into business');
        }).catch(err => showError(API.parseErrorResponse(err)));
    }
    return <li className="py-4">
        <div className="flex flex-row items-center">
            <div className="flex-grow" onClick={() => setAction('info')}>
                <p className="mb-2 first:font-bold text-brand">{business.name ?? <em className="text-muted">No name</em>}</p>
                <Grid items={[
                    [
                        <strong>User</strong>,
                        business?.user?.firstName ? `${business?.user?.firstName} ${business.user?.lastName}` : <NoValue />
                    ],
                    [
                        <strong>Plan</strong>,
                        business?.balance?.plan ?? <NoValue />
                    ],
                    [
                        <strong>Balance</strong>,
                        <Money pence={business?.balance?.retainedPence} colour />
                    ],
                    [
                        <strong>Registered</strong>,
                        <TimeAgo dateTime={business?.createdAt} />
                    ],
                    [
                        <strong>Last Active</strong>,
                        <TimeAgo dateTime={business?.user?.lastActive} />
                    ],
                    [
                        <strong>Onboarding</strong>,
                        <div className="flex flex-row gap-4 items-center">
                            <div className="h-4 w-[50%] border rounded-full bg-slate-50 overflow-clip">
                                <div className="h-full bg-brand rounded-r-full"
                                    style={{width: (business?.journey?.activity?.score ?? 0)+'%'}}
                                />
                            </div>
                            <div className="text-muted">
                                {business?.journey?.activity?.score ?? <NoValue />}
                            </div>
                        </div>
                    ]
                ]} />
            </div>
            <div className="flex-shrink-0">
                <Dropdown loading={switching}
                    options={[
                        {
                            title: 'More info',
                            icon: 'Info',
                            onClick: () => setAction('info'),
                        },
                        {
                            div: true,
                        },
                        {
                            title: 'Switch into',
                            icon: 'ArrowRightToBracket',
                            onClick: () => confirm(
                                {theme: 'danger', title: 'Switch into', message: `Switch into ${business.name}?`},
                                () => switchInto(),
                            ),
                        },
                        {
                            title: 'Send notification',
                            icon: 'Bell',
                            onClick: () => setAction('notification'),
                        }
                    ]}
                />
            </div>
        </div>

        {action === 'notification' && <SendNotification
            isOpen={action === 'notification'}
            setIsOpen={() => setAction(null)}
            business={business}
        />}
        {action === 'info' && <BusinessInfo
            isOpen={action === 'info'}
            setIsOpen={() => setAction(null)}
            business={business}
        />}
    </li>
}

export default Businesses;
